<template>
    <!-- The q-header was responsible for the ResizeObserver
        bug, so it has been replaced with a div. -->
    <div
        bordered
        class="bg-primary text-white header--container safe-area-padding sticky-navbar"
    >
        <!-- mobile device style navbar -->
        <q-toolbar
            v-if="$q.platform.is.mobile || $q.screen.lt.md"
            :class="
                $q.screen.lt.sm
                    ? 'xs-device-tool-bar mobile-height'
                    : 'default-height'
            "
        >
            <div class="row full-width items-center justify-between">
                <!-- left column -->
                <div class="col-4">
                    <!-- menu button -->
                    <q-btn
                        @click="$emit('menuClick')"
                        size="md"
                        dense
                        flat
                        round
                        icon="menu"
                    />
                    <!-- navigation button -->
                    <q-btn
                        v-if="breadcrumbs.length > 1"
                        @click="handleReturn"
                        dense
                        flat
                        :unelevated="true"
                        class="q-pl-none text-subtitle2"
                    >
                        <template v-slot:default>
                            <div>
                                <q-icon name="fas fa-chevron-left"></q-icon>
                                {{ $q.screen.lt.sm ? $t('Back') : returnLabel }}
                            </div>
                        </template>
                    </q-btn>
                </div>

                <!-- center column -->
                <div
                    class="col-4 text-center ellipsis text-subtitle1"
                    id="breadcrumb"
                >
                    <!-- title -->
                    {{ breadcrumbs.length > 0 ? pageTitle : '' }}
                </div>

                <!-- right column -->
                <div class="col-4 text-right">
                    <!-- full screen button -->
                    <q-btn
                        v-if="!$q.platform.is.mobile && $q.screen.gt.sm"
                        color="primary"
                        @click="$q.fullscreen.toggle()"
                        :icon="
                            $q.fullscreen.isActive
                                ? 'fas fa-compress'
                                : 'fas fa-expand'
                        "
                        :unelevated="true"
                        size="sm"
                        :class="$q.screen.lt.sm ? '' : 'q-mr-sm'"
                    ></q-btn>
                    <!-- facility button -->
                    <q-btn
                        v-show="$q.screen.gt.xs"
                        @click="
                            navigate({
                                name: 'facility_list'
                            })
                        "
                        class="q-mr-lg"
                        :unelevated="true"
                        dense
                        size="sm"
                        color="primary"
                        text-color="white"
                        icon="fas fa-industry-alt"
                    />

                    <!-- notification button -->
                    <q-btn
                        :class="$q.screen.gt.xs ? 'q-mr-lg' : 'q-mr-sm'"
                        @click="showNotificationPopupDialog"
                        :unelevated="true"
                        dense
                        size="sm"
                        color="primary"
                        text-color="white"
                        icon="fas fa-bell"
                    >
                        <q-badge
                            color="red"
                            rounded
                            floating
                            v-if="hasAnyUnresolvedNotifications"
                        />
                    </q-btn>

                    <!-- user profile button -->
                    <q-btn-dropdown
                        :label="$q.screen.gt.md ? username : ''"
                        dense
                        :ripple="false"
                        :unelevated="true"
                        class="primary"
                        :class="
                            $q.screen.lt.sm ? 'xs-device-user-profile-btn' : ''
                        "
                        icon="fas fa-user"
                        size="sm"
                        id="user-dropdown-button"
                    >
                        <q-list
                            v-for="(option, index) in userDropdownOptions"
                            :key="index"
                            :class="{
                                'active-route': $route.name === option.name
                            }"
                        >
                            <q-item
                                @click="navigate(option)"
                                v-close-popup
                                clickable
                                :id="
                                    option.label.toLowerCase() +
                                    '-dropdown-button'
                                "
                            >
                                <q-item-section side>
                                    <q-avatar
                                        class="user-dropdown"
                                        :class="{
                                            'active-route':
                                                $route.name === option.name
                                        }"
                                        :icon="option.icon"
                                    />
                                </q-item-section>
                                <q-item-section>
                                    <q-item-label class="user-dropdown">{{
                                        $t(option.label)
                                    }}</q-item-label>
                                </q-item-section>
                            </q-item>
                        </q-list>
                        <q-item dense>
                            <q-item-section class="q-mb-sm">
                                <q-item-label caption class="text-center">{{
                                    version.replace('v', 'Version ')
                                }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-btn-dropdown>
                </div>
            </div>
        </q-toolbar>

        <!-- Desktop style navbar -->
        <q-toolbar v-else>
            <q-btn @click="$emit('menuClick')" dense flat round icon="menu" />
            <q-toolbar-title class="q-mr-md">
                <q-btn
                    id="lean-logo-button"
                    v-if="$q.screen.gt.xs"
                    :unelevated="true"
                    @click="
                        navigate({
                            name: 'facility_list'
                        })
                    "
                    color="primary"
                    no-caps
                    dense
                    stack
                >
                    <div class="iotorq-logo">
                        <img
                            class="vertical-middle"
                            src="@assets/lean_logo.svg"
                            alt="ioTORQ LEAN Logo"
                        />
                    </div>
                </q-btn>
            </q-toolbar-title>
            <!-- full screen button -->
            <q-btn
                color="primary"
                @click="$q.fullscreen.toggle()"
                :icon="
                    $q.fullscreen.isActive ? 'fas fa-compress' : 'fas fa-expand'
                "
                :label="
                    $q.fullscreen.isActive && $q.screen.gt.xs
                        ? $t('Exit Fullscreen')
                        : $t('Enter Fullscreen')
                "
                class="top-navbar-options"
                :unelevated="true"
            ></q-btn>
            <!-- facility button -->
            <q-btn
                :label="$q.screen.gt.xs ? $t('Facilities') : ''"
                :unelevated="true"
                @click="navigate({ name: 'facility_list' })"
                class="top-navbar-options"
                color="primary"
                icon="fas fa-industry-alt"
            />
            <q-btn
                v-if="selectedFacility && !$isUserPlanner()"
                :unelevated="true"
                @click="showNotificationPopupDialog"
                class="top-navbar-options"
                color="primary"
            >
                <q-icon name="fas fa-bell" />
                <!-- Label-->
                <span class="q-ml-sm" style="font-size: 16px">
                    {{ $t('Justification Requests') }}
                    <q-badge
                        align="top"
                        color="red"
                        rounded
                        v-if="hasAnyUnresolvedNotifications"
                /></span>
            </q-btn>
            <!-- User dropdown -->
            <q-btn-dropdown
                :label="$q.screen.gt.xs ? username : ''"
                :ripple="false"
                :unelevated="true"
                class="primary top-navbar-options"
                icon="fas fa-user"
                id="user-dropdown-button"
            >
                <q-list
                    v-for="(option, index) in userDropdownOptions"
                    :key="index"
                    :class="{
                        'active-route': $route.name === option.name
                    }"
                >
                    <q-item
                        clickable
                        @click="navigate(option)"
                        v-close-popup
                        :id="option.id"
                    >
                        <q-item-section side>
                            <q-avatar
                                class="user-dropdown"
                                :class="{
                                    'active-route': $route.name === option.name
                                }"
                                :icon="option.icon"
                            />
                        </q-item-section>
                        <q-item-section>
                            <q-item-label class="user-dropdown">{{
                                $t(option.label)
                            }}</q-item-label>
                        </q-item-section>
                    </q-item>
                </q-list>
                <div
                    class="text-center q-mb-md q-mt-sm text-secondary text-caption"
                >
                    {{ version.replace('v', 'Version ') }}
                </div>
            </q-btn-dropdown>
        </q-toolbar>
    </div>
</template>

<script>
import appVersion from '@baseDir/appVersion.json';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { getHelpUrl } from '@models/urls.js';
import { openURL } from 'quasar';
import { mapState, mapActions } from 'pinia';
import { useAuthStore } from '@stores/auth';
import { useNotificationsStore } from '@stores/notifications.ts';
import { useFacilitiesStore } from '@src/stores/facilities';

export default {
    name: 'NavBar',
    mixins: [BreadcrumbsManager],
    emits: ['navigate', 'menuClick'],
    data() {
        return {
            userDropdownOptions: [
                {
                    icon: 'fas fa-user',
                    label: 'Profile',
                    name: 'profile',
                    id: 'profile-dropdown-button'
                },
                {
                    icon: 'fas fa-cogs',
                    label: 'Settings',
                    name: 'settings',
                    params: {},
                    id: 'settings-dropdown-button'
                },
                {
                    icon: 'fas fa-question-circle',
                    label: 'Help',
                    name: 'help',
                    params: {},
                    id: 'help-dropdown-button'
                },
                {
                    icon: 'fas fa-sign-out-alt',
                    label: 'Logout',
                    name: 'login',
                    params: {},
                    id: 'logout-dropdown-button'
                }
            ],

            // app versioning
            version: appVersion.version
        };
    },
    computed: {
        ...mapState(useFacilitiesStore, ['selectedFacility']),
        ...mapState(useNotificationsStore, {
            hasAnyUnresolvedNotifications: 'hasAnyUnresolvedNotifications'
        }),
        ...mapState(useAuthStore, ['user']),
        /**
         * Page title of active view.
         * @return {string}
         */
        pageTitle() {
            const breadcrumb = this.breadcrumbs[this.breadcrumbs.length - 1];
            return breadcrumb['label'];
        },
        /**
         * Page title of previous view.
         * @return {string}
         */
        returnLabel() {
            const breadcrumb = this.breadcrumbs[this.breadcrumbs.length - 2];
            return breadcrumb['label'];
        },
        /**
         * Username of current user.
         * @return {string}
         */
        username() {
            let username = '';
            if (this.user) {
                username = this.user.username;
            }
            return username;
        }
    },
    methods: {
        ...mapActions(useNotificationsStore, ['showNotificationPopupAction']),
        /**
         * Handles route navigation.
         * @param {Object} route
         * @param {string} route.name
         * @param {Object} route.params
         */
        navigate(route) {
            // when navigating to user manual, set destination based on user's language
            if (route.name === 'help') {
                const helpUrl = getHelpUrl(this.user.language);
                // Open new tab for desktop and open in browser for mobile.
                // Using Quasar's openURL function to open the link in the browser for mobile devices compatibility.
                // https://quasar.dev/quasar-utils/other-utils#Open-External-URL
                openURL(
                    helpUrl,
                    // Callback function if the URL does not open
                    undefined,
                    // Options for the window.open() function
                    {
                        // Block the new window or tab to access the window.opener property of the parent window.
                        noopener: true,
                        // Do not send the Referer header when the new window or tab is opened.
                        noreferrer: true,
                        menubar: this.$q.platform.is.mobile,
                        toolbar: this.$q.platform.is.mobile
                    }
                );
            } else {
                // navigate
                this.$emit('navigate', route);
            }
        },
        /**
         * Navigate to previously visited view.
         */
        handleReturn() {
            // get second last item on the breadcrumb stack
            const breadcrumb = this.breadcrumbs[this.breadcrumbs.length - 2];
            this.$router.push({
                name: breadcrumb.route.name,
                params: breadcrumb.route.params,
                query: breadcrumb.route.query
            });
        },
        /**
         * Show the notification popup dialog.
         */
        showNotificationPopupDialog() {
            if (this.hasAnyUnresolvedNotifications) {
                this.showNotificationPopupAction(true);
            } else {
                this.$q.notify({
                    position: 'top',
                    type: 'positive',
                    timeout: 1000,
                    message: this.$t('No new notifications')
                });
            }
        }
    }
};
</script>

<style lang="scss" scoped>
// In order to fix the ResizeObserver Loop
// error the q-header has been replaced with a div,
// so the styles for the header have been added here.
.header--container {
    position: relative;
    z-index: 4000 !important;
    border-bottom: 1px solid #0000001f;
}

.active-route {
    color: $primary;
    font-weight: 600;
}

.iotorq-logo {
    cursor: pointer;
}

.iotorq-logo img {
    max-width: 110px;
}

.version-tag {
    font-size: 11px;
    font-weight: 500;
    line-height: 1;
    width: 90%;
    text-align: right;
}

.top-navbar-options {
    font-weight: 500;
    margin: 0px 4px;
    padding: 10px;
}

.xs-device-tool-bar {
    min-height: 40px;
    padding: 0 5px;
}

.xs-device-user-profile-btn {
    padding: 5px;
    margin: 0;
}

.mobile-height {
    min-height: $navbar-height-mobile;
}

.default-height {
    min-height: $navbar-height;
}

// On mobile devices, the navbar should stick to the top of the screen.
.sticky-navbar {
    position: fixed;
    top: 0;
    width: 100%;
    background: $primary;
}
</style>
